import type { Ref } from "vue";

/**
 * Minimal YouTube IFrame Player wrapper for picking time ranges: mounts a
 * player into `target`, exposes current time / seek / duration, and reports
 * embed errors (101/150 = the owner disabled embedding) so callers can fall
 * back to manual input.
 */

type YoutubePlayer = {
  getCurrentTime?: () => number;
  getDuration?: () => number;
  seekTo?: (seconds: number, allowSeekAhead: boolean) => void;
  pauseVideo?: () => void;
  destroy?: () => void;
};

declare global {
  interface Window {
    YT?: { Player: new (el: HTMLElement, options: Record<string, unknown>) => YoutubePlayer };
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<NonNullable<Window["YT"]>> | null = null;

const loadYoutubeIframeApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previous?.();
        if (window.YT) resolve(window.YT);
      };
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.onerror = () => {
        apiPromise = null;
        reject(new Error("Failed to load the YouTube player"));
      };
      document.head.appendChild(script);
    });
  }
  return apiPromise;
};

export const useYoutubePlayer = (
  target: Ref<HTMLElement | null>,
  videoId: Ref<string>
) => {
  const ready = ref(false);
  const error = ref<string | null>(null);
  const duration = ref(0);
  let player: YoutubePlayer | null = null;
  // Guards against a slow API load finishing after the target changed.
  let generation = 0;

  const destroy = () => {
    generation += 1;
    try {
      player?.destroy?.();
    } catch {
      // The iframe may already be gone with its container.
    }
    player = null;
    ready.value = false;
  };

  const readDuration = () => {
    const seconds = Number(player?.getDuration?.()) || 0;
    if (seconds > 0) duration.value = seconds;
  };

  const create = async () => {
    destroy();
    const el = target.value;
    const id = videoId.value;
    // Keep a reported error while the container is unmounted: callers hide
    // it on error, and clearing here would remount it and loop.
    if (!el || !id) return;
    error.value = null;

    const current = generation;
    try {
      const YT = await loadYoutubeIframeApi();
      if (current !== generation) return;
      // The API replaces the node it is given; keep the Vue-owned container.
      const mount = document.createElement("div");
      el.replaceChildren(mount);
      player = new YT.Player(mount, {
        host: "https://www.youtube-nocookie.com",
        videoId: id,
        width: "100%",
        height: "100%",
        playerVars: { playsinline: 1, rel: 0, origin: window.location.origin },
        events: {
          onReady: () => {
            if (current !== generation) return;
            ready.value = true;
            readDuration();
          },
          onStateChange: readDuration,
          onError: (event: { data?: number }) => {
            if (current !== generation) return;
            error.value = `${event?.data ?? "unknown"}`;
          },
        },
      });
    } catch {
      if (current === generation) error.value = "load_failed";
    }
  };

  watch(videoId, () => {
    error.value = null;
    duration.value = 0;
  });
  watch([target, videoId], create, { immediate: true });
  onBeforeUnmount(destroy);

  return {
    ready,
    error,
    duration,
    getCurrentTime: () => Number(player?.getCurrentTime?.()) || 0,
    seekTo: (seconds: number) => player?.seekTo?.(Math.max(0, seconds), true),
  };
};
